import React, { useState, useEffect } from 'react';
import { Send, Sparkles, Star } from 'lucide-react';
import { toast } from 'sonner';

const Newsletter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('newsletter-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      
      if (data.status === 'already_subscribed') {
        toast.info("You're already part of our crystal community!", {
          description: 'Check your inbox for our latest crystal wisdom.',
        });
      } else {
        toast.success('Welcome to our spiritual community!', {
          description: 'Check your inbox for exclusive offers and crystal wisdom.',
        });
      }
      setEmail('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="newsletter-section" className="newsletter-section relative py-24 px-6 overflow-hidden">
      {/* Prismatic light effects */}
      <div className="absolute top-10 left-10 w-64 h-64 bg-[#9D4EDD]/20 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#FF6B9D]/15 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FFB800]/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '2s' }} />

      <div className="max-w-4xl mx-auto relative z-10">
        <div className={`glass-iridescent rounded-3xl p-8 md:p-12 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Crystal decoration */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] flex items-center justify-center shadow-xl animate-float">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] blur-xl opacity-40" />
            </div>
          </div>

          <span className="section-eyebrow">
            Join Our Crystal Community
          </span>
          
          <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A2E] mb-4">
            Receive <span className="text-gradient-vibrant">Sacred Crystal</span> Wisdom
          </h2>
          
          <p className="text-[#4A4A6A] max-w-xl mx-auto mb-8 leading-relaxed">
            Subscribe to receive exclusive offers, new arrival alerts, 
            crystal care tips, and monthly moon phase guides for your spiritual journey.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="newsletter-input"
              required
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-4 rounded-xl hover:shadow-[0_10px_30px_rgba(157,78,221,0.4)] hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold tracking-wider text-sm uppercase"
              data-testid="newsletter-subscribe-btn"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Subscribe
                  <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-[#7A7A9A] mt-4">
            Unsubscribe anytime. We respect your privacy and energy.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
